/**
 * Light or dark: what the user asked for, and what is on screen.
 *
 * The preference is per device, in `localStorage`, never on the account: the
 * sign-in screen has to be drawn before anyone is known. Storage is best effort
 * and silent on failure, like `draftStorage` — a browser that refuses it just
 * gets the default.
 *
 * `index.html` carries an inline copy of `readThemePreference` + `resolveTheme`
 * that runs before first paint, so a reload never flashes the wrong theme. Keep
 * the key, the values and the default in step with it.
 */

export type ThemePreference = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'outpost:theme';

/** Follow the device until the user picks a side in Settings. */
export const DEFAULT_THEME_PREFERENCE: ThemePreference = 'system';

const DARK_QUERY = '(prefers-color-scheme: dark)';

/** `<meta name="theme-color">` per theme: the page tone, so mobile chrome matches. */
const THEME_COLORS: Record<ResolvedTheme, string> = {
  light: '#f6f7f9',
  dark: '#0d0f13',
};

function isPreference(value: unknown): value is ThemePreference {
  return value === 'system' || value === 'light' || value === 'dark';
}

export function readThemePreference(): ThemePreference {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isPreference(stored) ? stored : DEFAULT_THEME_PREFERENCE;
  } catch {
    return DEFAULT_THEME_PREFERENCE;
  }
}

export function writeThemePreference(preference: ThemePreference): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // Best effort: the choice still applies for this page's lifetime.
  }
}

/** The media query `system` follows. Null where `matchMedia` is missing. */
export function darkSchemeQuery(): MediaQueryList | null {
  return typeof window.matchMedia === 'function' ? window.matchMedia(DARK_QUERY) : null;
}

export function resolveTheme(preference: ThemePreference): ResolvedTheme {
  if (preference !== 'system') return preference;
  return darkSchemeQuery()?.matches ? 'dark' : 'light';
}

/** Puts a resolved theme on `<html>`, where every colour token keys off it. */
export function applyTheme(theme: ResolvedTheme): void {
  const root = document.documentElement;
  root.dataset.theme = theme;
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', THEME_COLORS[theme]);
}
